// components/Outreach/OutreachDashboard.js
import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { getCookie } from "../../services/cookie";

// ─── Lazy-import тяжёлых табов ──────────────────────────────────
// Грузим только те компоненты, что нужны для активного таба.
// На первом рендере это только CampaignsList (~700 строк),
// остальное (WorkerPanel + FlowKit + StatsTab + reactflow) не парсится.
const CampaignsList = lazy(() =>
  import("./CampaignsList").then(m => ({ default: m.CampaignsList }))
);
const CreateCampaign = lazy(() =>
  import("./CreateCampaign").then(m => ({ default: m.CreateCampaign }))
);
const IntentionsList = lazy(() =>
  import("./IntentionsList").then(m => ({ default: m.IntentionsList }))
);
const CreateIntentionsForCampaign = lazy(() =>
  import("./CreateIntentionsForCampaign").then(m => ({
    default: m.CreateIntentionsForCampaign,
  }))
);
const WorkerPanel = lazy(() =>
  import("./WorkerPanel").then(m => ({ default: m.WorkerPanel }))
);
const StatsTab = lazy(() =>
  import("./StatsTab").then(m => ({ default: m.StatsTab }))
);
const TaskBundlesTab = lazy(() =>
  import("./TaskBundlesTab").then(m => ({ default: m.TaskBundlesTab }))
);
const ExecutionTab = lazy(() =>
  import("./ExecutionTab").then(m => ({ default: m.ExecutionTab }))
);
const RepliesTab = lazy(() =>
  import("./RepliesTab").then(m => ({ default: m.RepliesTab }))
);
const ReplyIntentionsTab = lazy(() =>
  import("./ReplyIntentionsTab").then(m => ({ default: m.ReplyIntentionsTab }))
);

const API_BASE = "https://flautreach.com/flautreach/api";

const CAMPAIGN_TYPE_LABELS = {
  email: "📧 Email",
  contact: "📝 Contact form",
  calendly: "📅 Calendly",
  emailAndContact: "✉️ Email + Contact",
};

// ─── Fallback для Suspense ────────────────────────────────────
function TabFallback() {
  return (
    <div style={{ textAlign: "center", padding: 60 }}>
      <div className="spinner" />
      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 10 }}>
        Загрузка вкладки…
      </div>
    </div>
  );
}

export function OutreachDashboard({ add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return {
      "Content-Type": "application/json",
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    };
  };

  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem("outreachActiveTab") || "campaigns"
  );
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showCreateIntentions, setShowCreateIntentions] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ─── Статистика ──────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showCampaignsList, setShowCampaignsList] = useState(false);

  useEffect(() => {
    localStorage.setItem("outreachActiveTab", activeTab);
  }, [activeTab]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/outreach/intentions/stats/`, {
        headers: getHeaders(),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setStats(await resp.json());
    } catch (err) {
      console.error("Stats error:", err);
      add?.("Ошибка загрузки статистики", "error");
    } finally {
      setStatsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [add]);

  // ─── Отложенная загрузка статистики ──────────────────
  // На первом монтировании статистика грузится через 150 мс,
  // чтобы CampaignsList успел отрисоваться и сделать свой fetch.
  // При ручном refresh (refreshKey > 0) — сразу, без задержки.
  useEffect(() => {
    if (refreshKey === 0) {
      const id = setTimeout(() => loadStats(), 150);
      return () => clearTimeout(id);
    }
    loadStats();
  }, [refreshKey, loadStats]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // ─── Достаём поля из ответа ──────────────────────────
  const it = stats?.intentions || {};
  const pending = it.pending ?? 0;
  const pendingRows = it.pending_rows ?? 0;
  const pendingDoubled = it.pending_doubled ?? 0;
  const done = it.done ?? 0;
  const doneRows = it.done_rows ?? 0;
  const inProgress = it.in_progress ?? 0;
  const failed = it.failed ?? 0;
  const skipped = it.skipped ?? 0;
  const campaignsWithPending = it.campaigns_with_pending ?? 0;
  const campaignsList = it.campaigns_pending_list || [];
  const byType = it.by_campaign_type || {};

  const loadingStr = (val) =>
    statsLoading && !stats ? "…" : val.toLocaleString("ru-RU");

  return (
    <div className="outreach-dashboard">
      <div className="rules-header">
        <div>
          <h2>📢 Outreach System</h2>
          <p>Управление аутрич-кампаниями</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn-add"
            onClick={() => {
              setShowCreateCampaign(true);
              setShowCreateIntentions(false);
            }}
          >
            + Новая кампания
          </button>
        </div>
      </div>

      {showCreateCampaign ? (
        <Suspense fallback={<TabFallback />}>
          <CreateCampaign
            onBack={() => {
              setShowCreateCampaign(false);
              refresh();
            }}
            onCreated={() => {
              setShowCreateCampaign(false);
              refresh();
            }}
            add={add}
          />
        </Suspense>
      ) : showCreateIntentions && selectedCampaign ? (
        <Suspense fallback={<TabFallback />}>
          <CreateIntentionsForCampaign
            campaign={selectedCampaign}
            onBack={() => {
              setShowCreateIntentions(false);
              setSelectedCampaign(null);
              refresh();
            }}
            onCreated={() => {
              setShowCreateIntentions(false);
              setSelectedCampaign(null);
              refresh();
            }}
            add={add}
          />
        </Suspense>
      ) : (
        <>
          {/* ═══ Три главные карточки ═══ */}
          <div className="outreach-stats-grid">
            <div
              className="outreach-stat-card"
              onClick={() => setActiveTab("intentions")}
              style={{ cursor: "pointer", borderLeft: "4px solid #f59e0b" }}
            >
              <div className="stat-icon">⏳</div>
              <div className="stat-value" style={{ color: "#f59e0b" }}>
                {loadingStr(pending)}
              </div>
              <div className="stat-label">В очереди</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                таргетов · {pendingRows.toLocaleString("ru-RU")} интеншенов
                {pendingDoubled > 0 && ` · ${pendingDoubled} удв.`}
              </div>
            </div>

            <div
              className="outreach-stat-card"
              style={{ borderLeft: "4px solid #16a34a" }}
            >
              <div className="stat-icon">✅</div>
              <div className="stat-value" style={{ color: "#16a34a" }}>
                {loadingStr(done)}
              </div>
              <div className="stat-label">Выполнено</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                таргетов · {doneRows.toLocaleString("ru-RU")} интеншенов
              </div>
            </div>

            <div
              className="outreach-stat-card"
              onClick={() => setShowCampaignsList((v) => !v)}
              style={{ cursor: "pointer", borderLeft: "4px solid #3b71fe" }}
            >
              <div className="stat-icon">📋</div>
              <div className="stat-value" style={{ color: "#3b71fe" }}>
                {loadingStr(campaignsWithPending)}
              </div>
              <div className="stat-label">Кампаний в работе</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                с активной очередью{" "}
                {campaignsList.length > 0 ? (showCampaignsList ? "▲" : "▼") : ""}
              </div>
            </div>
          </div>

          {/* ═══ Строка статусов ═══ */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "8px 12px",
              marginBottom: 16,
              background: "#f8f9ff",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              fontSize: 12,
              color: "#6b7280",
              flexWrap: "wrap",
            }}
          >
            <span>
              <strong>{statsLoading && !stats ? "…" : inProgress}</strong> в
              процессе
            </span>
            <span>
              <strong>{statsLoading && !stats ? "…" : failed}</strong> failed
            </span>
            <span>
              <strong>{statsLoading && !stats ? "…" : skipped}</strong> skipped
            </span>
            <button
              onClick={() => setShowBreakdown((v) => !v)}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#3b71fe",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {showBreakdown ? "▲ Скрыть по типам" : "▼ По типам кампаний"}
            </button>
            <button
              onClick={loadStats}
              disabled={statsLoading}
              title="Обновить статистику"
              style={{
                background: "none",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                cursor: statsLoading ? "wait" : "pointer",
                padding: "4px 8px",
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              {statsLoading ? "…" : "↻"}
            </button>
          </div>

          {/* ═══ Список кампаний с pending ═══ */}
          {showCampaignsList && campaignsList.length > 0 && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#374151",
                }}
              >
                Кампании с очередью ({campaignsList.length})
              </div>
              {campaignsList.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    fontSize: 12,
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <span style={{ flex: 1, fontWeight: 500 }}>{c.name}</span>
                  <span style={{ color: "#6b7280", fontSize: 11 }}>
                    {c.targets} тарг.
                  </span>
                  <span style={{ color: "#6b7280", fontSize: 11 }}>
                    {c.intentions} инт.
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ═══ Разбивка по типам ═══ */}
          {showBreakdown && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#374151",
                }}
              >
                Разбивка по типу кампании
              </div>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={thStyle}>Тип</th>
                      <th style={thStyle}>В очереди</th>
                      <th style={thStyle}>Выполнено</th>
                      <th style={thStyle}>Failed</th>
                      <th style={thStyle}>Кампаний</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(byType).map(([type, d]) => (
                      <tr key={type}>
                        <td style={tdStyle}>
                          {CAMPAIGN_TYPE_LABELS[type] || type}
                        </td>
                        <td style={tdStyle}>
                          <strong>{d.pending?.targets ?? 0}</strong>
                          <span style={tdHint}>
                            {" "}
                            тарг. / {d.pending?.intentions ?? 0} инт.
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <strong>{d.done?.targets ?? 0}</strong>
                          <span style={tdHint}>
                            {" "}
                            / {d.done?.intentions ?? 0} инт.
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <strong>{d.failed?.targets ?? 0}</strong>
                        </td>
                        <td style={tdStyle}>
                          {d.campaigns_with_pending ?? 0}
                        </td>
                      </tr>
                    ))}
                    {Object.keys(byType).length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            ...tdStyle,
                            color: "#9ca3af",
                            textAlign: "center",
                          }}
                        >
                          Нет данных
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Табы */}
          <div className="outreach-tabs">
            <button
              className={`outreach-tab ${
                activeTab === "campaigns" ? "active" : ""
              }`}
              onClick={() => setActiveTab("campaigns")}
            >
              📢 Кампании
            </button>
            <button
              className={`outreach-tab ${
                activeTab === "intentions" ? "active" : ""
              }`}
              onClick={() => setActiveTab("intentions")}
            >
              📋 Намерения ({pendingRows})
            </button>
            <button
              className={`outreach-tab ${
                activeTab === "worker" ? "active" : ""
              }`}
              onClick={() => setActiveTab("worker")}
            >
              👷 Воркер
            </button>
            <button
              className={`outreach-tab ${
                activeTab === "replies" ? "active" : ""
              }`}
              onClick={() => setActiveTab("replies")}
            >
              📥 Добавить Ответы
            </button>
            <button
              className={`outreach-tab ${
                activeTab === "replyint" ? "active" : ""
              }`}
              onClick={() => setActiveTab("replyint")}
            >
              📋 Реплай Интеншены
            </button>
            <button
              className={`outreach-tab ${
                activeTab === "bundles" ? "active" : ""
              }`}
              onClick={() => setActiveTab("bundles")}
            >
              🎯 Связки
            </button>
            <button
              className={`outreach-tab ${
                activeTab === "stats" ? "active" : ""
              }`}
              onClick={() => setActiveTab("stats")}
            >
              📊 Статистика
            </button>
          </div>

          <Suspense fallback={<TabFallback />}>
            {activeTab === "campaigns" && (
              <CampaignsList
                onRefresh={refresh}
                onCreateIntentions={(campaign) => {
                  setSelectedCampaign(campaign);
                  setShowCreateIntentions(true);
                }}
                add={add}
              />
            )}
            {activeTab === "intentions" && (
              <IntentionsList onRefresh={refresh} add={add} />
            )}
            {activeTab === "worker" && <WorkerPanel add={add} />}
            {activeTab === "executions" && <ExecutionTab add={add} />}
            {activeTab === "replies" && <RepliesTab add={add} />}
            {activeTab === "replyint" && <ReplyIntentionsTab add={add} />}
            {activeTab === "bundles" && <TaskBundlesTab add={add} />}
            {activeTab === "stats" && <StatsTab add={add} />}
          </Suspense>
        </>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: "1px solid #e5e7eb",
  color: "#6b7280",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: ".03em",
};
const tdStyle = { padding: "8px", borderBottom: "1px solid #f3f4f6" };
const tdHint = { color: "#9ca3af", fontSize: 11 };